import { AppLocale, DEFAULT_LOCALE, resolveSupportedLocale } from './locales';
import { resolveLocaleFromServer } from './serverLocale';

export const LOCALE_PREFERENCE_STORAGE_KEY = 'ft.locale';
const LOCALE_PREFERENCE_COOKIE_NAME = 'ft_locale';
const LOCALE_PREFERENCE_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

const canUseWindow = () => typeof window !== 'undefined';
const canUseDocument = () => typeof document !== 'undefined';

const readLocalePreferenceCookie = (): string | null => {
  if (!canUseDocument()) {
    return null;
  }

  const cookie = document.cookie
    .split(';')
    .map((entry) => entry.trim())
    .find((entry) => entry.startsWith(`${LOCALE_PREFERENCE_COOKIE_NAME}=`));

  if (!cookie) {
    return null;
  }

  return decodeURIComponent(cookie.split('=').slice(1).join('='));
};

export const readStoredLocalePreference = (): AppLocale | null => {
  const storageValue = canUseWindow() ? window.localStorage.getItem(LOCALE_PREFERENCE_STORAGE_KEY) : null;
  const cookieValue = readLocalePreferenceCookie();
  const preferredLocale = storageValue || cookieValue;

  if (!preferredLocale) {
    return null;
  }

  return resolveSupportedLocale(preferredLocale);
};

export const readBrowserLocalePreference = (): AppLocale | null => {
  if (typeof navigator === 'undefined') {
    return null;
  }

  const candidates = Array.isArray(navigator.languages) && navigator.languages.length > 0
    ? navigator.languages
    : [navigator.language];

  const firstCandidate = candidates.find((value) => typeof value === 'string' && value.trim().length > 0);
  return firstCandidate ? resolveSupportedLocale(firstCandidate) : null;
};

export const resolveInitialLocalePreference = (): AppLocale => {
  return readStoredLocalePreference() ?? readBrowserLocalePreference() ?? DEFAULT_LOCALE;
};

export const resolveInitialLocalePreferenceAsync = async (): Promise<AppLocale> => {
  const serverResolvedLocale = await resolveLocaleFromServer();
  if (serverResolvedLocale?.source === 'account') {
    return serverResolvedLocale.locale;
  }

  const storedLocale = readStoredLocalePreference();
  if (storedLocale) {
    return storedLocale;
  }

  if (serverResolvedLocale?.source === 'accept-language') {
    return serverResolvedLocale.locale;
  }

  return readBrowserLocalePreference() ?? DEFAULT_LOCALE;
};

export const persistLocalePreference = (locale: string): AppLocale => {
  const resolvedLocale = resolveSupportedLocale(locale);

  if (canUseWindow()) {
    window.localStorage.setItem(LOCALE_PREFERENCE_STORAGE_KEY, resolvedLocale);
  }

  if (canUseDocument()) {
    document.cookie = `${LOCALE_PREFERENCE_COOKIE_NAME}=${encodeURIComponent(resolvedLocale)}; path=/; max-age=${LOCALE_PREFERENCE_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
  }

  return resolvedLocale;
};

export const clearStoredLocalePreference = (): void => {
  if (canUseWindow()) {
    window.localStorage.removeItem(LOCALE_PREFERENCE_STORAGE_KEY);
  }

  if (canUseDocument()) {
    document.cookie = `${LOCALE_PREFERENCE_COOKIE_NAME}=${encodeURIComponent(DEFAULT_LOCALE)}; path=/; max-age=0; SameSite=Lax`;
  }
};
